/**
 * FeedbackWidget — global floating button + modal.
 * Captures: text, severity, optional screenshot, current route, portal
 * context. POSTs to /api/feedback/v2/submit. Lightweight confirmation.
 *
 * Doctrine §"User Experience" (Feedback Routing, May 16, 2026):
 *   - Lightweight confirmation
 *   - No "24h guarantee" / corporate ticket language
 *   - Human and quiet
 *
 * Auto-mounted from App.js on every authed route.
 */
import React, { useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { MessageSquare, Camera, Send, Loader2, X, Image } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SEVERITY_OPTIONS = [
  { v: 'low',      label: 'Just a note',    color: 'bg-slate-100 text-slate-700' },
  { v: 'normal',   label: 'Something off',  color: 'bg-blue-100 text-blue-700' },
  { v: 'high',     label: 'Broken',         color: 'bg-amber-100 text-amber-700' },
  { v: 'critical', label: 'Blocking me',    color: 'bg-red-100 text-red-700' },
];


const FeedbackWidget = () => {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [severity, setSeverity] = useState('normal');
  const [screenshotPreview, setScreenshotPreview] = useState(null);
  const [screenshotBase64, setScreenshotBase64] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  // Skip on public/landing/login surfaces
  const skipPaths = ['/', '/landing', '/login', '/signup', '/forgot-password',
                     '/reset-password', '/super-admin/login', '/pay/',
                     '/remodify/', '/customer-portal/'];
  const onSkipped = skipPaths.some((p) => location.pathname.startsWith(p));
  if (onSkipped) return null;

  // Hide for unauthed sessions
  const token = localStorage.getItem('auth_token')
    || localStorage.getItem('access_token');
  if (!token) return null;

  const reset = () => {
    setText(''); setSeverity('normal');
    setScreenshotPreview(null); setScreenshotBase64(null);
  };

  const onScreenshot = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setScreenshotPreview(ev.target.result);
      setScreenshotBase64(ev.target.result.split(',')[1]);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const submit = async () => {
    if (text.trim().length < 3) {
      toast.error('Tell us a little more'); return;
    }
    setSubmitting(true);
    try {
      const portalSource =
        localStorage.getItem('user_role') ||
        (JSON.parse(localStorage.getItem('analytics_user') || '{}').role) ||
        'business';
      const r = await axios.post(`${API}/feedback/v2/submit`, {
        text,
        rating: severity === 'critical' ? 1 : severity === 'high' ? 2
                : severity === 'normal' ? 3 : 5,
        user_severity: severity,
        route: location.pathname,
        portal_source: String(portalSource).toLowerCase(),
        screenshot_base64: screenshotBase64,
        screenshot_filename: screenshotBase64 ? 'feedback.png' : null,
        extra_context: {
          user_agent: navigator.userAgent.slice(0, 200),
          screen_w: window.screen?.width,
          screen_h: window.screen?.height,
          referrer: document.referrer || null,
        },
      });
      toast.success(r.data?.message
        || 'Feedback received — our team will review this.');
      setOpen(false); reset();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send feedback');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-5 right-5 z-40 h-12 w-12 rounded-full bg-slate-900 hover:bg-slate-800 text-white shadow-lg flex items-center justify-center transition-transform active:scale-95"
        aria-label="Send feedback"
        data-testid="feedback-widget-fab"
      >
        <MessageSquare className="w-5 h-5" />
      </button>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
        <DialogContent className="max-w-md" data-testid="feedback-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-emerald-600" />
              Send Feedback
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
                What's going on?
              </Label>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Describe the issue, suggestion, or thing you saw…"
                rows={4}
                className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm outline-none focus:border-emerald-500 resize-none"
                data-testid="feedback-text-input"
              />
            </div>

            <div>
              <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
                Severity
              </Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                {SEVERITY_OPTIONS.map((o) => (
                  <button
                    key={o.v}
                    onClick={() => setSeverity(o.v)}
                    className={`px-3 py-2 rounded-lg text-xs font-semibold transition-all ${
                      severity === o.v ? `${o.color} ring-2 ring-offset-1 ring-slate-300`
                                       : 'bg-slate-50 text-slate-400 hover:bg-slate-100'
                    }`}
                    data-testid={`feedback-severity-${o.v}`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
                Screenshot (optional)
              </Label>
              <input
                type="file"
                accept="image/*"
                ref={fileRef}
                onChange={onScreenshot}
                className="hidden"
              />
              {screenshotPreview ? (
                <div className="relative mt-1">
                  <img src={screenshotPreview} alt="screenshot"
                       className="w-full h-32 object-cover rounded-lg border border-slate-200" />
                  <button
                    onClick={() => { setScreenshotPreview(null); setScreenshotBase64(null); }}
                    className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full"
                    data-testid="feedback-screenshot-remove"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full mt-1 h-9"
                  onClick={() => fileRef.current?.click()}
                  data-testid="feedback-attach-screenshot"
                >
                  <Image className="w-4 h-4 mr-2" /> Attach Screenshot
                </Button>
              )}
            </div>

            <div className="text-[11px] text-slate-400 leading-relaxed">
              We'll automatically include the page you're on
              ({location.pathname}) and your account so the team has
              context.
            </div>

            <Button
              onClick={submit}
              disabled={submitting}
              className="w-full bg-emerald-600 hover:bg-emerald-700"
              data-testid="feedback-submit-btn"
            >
              {submitting
                ? <Loader2 className="w-4 h-4 animate-spin mr-2" />
                : <Send className="w-4 h-4 mr-2" />}
              Send Feedback
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default FeedbackWidget;
